/**
 * Heuristic: distinguish PI person names from job titles. Keep logic aligned with
 * CTMS.API/Helpers/PrincipalInvestigatorNameHeuristic.cs
 */

const ROLE_WORDS = new Set([
  'leading', 'head', 'chief', 'senior', 'principal', 'consultant', 'associate',
  'assistant', 'deputy', 'director', 'coordinator', 'manager', 'department',
  'medical', 'clinical', 'oncology', 'oncologist', 'physician', 'specialist',
  'gastroenterologist', 'cardiologist', 'hematologist', 'research', 'trials',
  'unit', 'center', 'centre', 'institute', 'division', 'section',
  'faculty', 'chair', 'investigator', 'surgeon', 'surgery', 'radiologist',
  'pathologist', 'nephrologist', 'neurologist', 'pulmonologist', 'dermatologist',
  'hospital', 'university', 'college', 'trust', 'foundation', 'group', 'team',
  'breast', 'gi', 'gastrointestinal', 'her2',
]);

const STOP_WORDS = new Set([
  'of', 'the', 'and', 'for', 'in', 'at', 'to', 'a', 'an', 'or',
]);

const NAME_PARTICLES = new Set([
  'van', 'von', 'de', 'la', 'le', 'der', 'den', 'ten', 'ter', 'da', 'del', 'di', 'du', 'dos',
]);

const HONORIFIC_PREFIX = /^(dr\.?|prof\.?|professor|mr\.?|mrs\.?|ms\.?|miss)\s+(.+)$/i;
const LETTER_TOKENS = /[\p{L}]+/gu;
const ROLE_LEAD = /^(leading|head|chief|senior|principal|associate|assistant)\s+/i;

function letterTokenMatches(text) {
  const re = new RegExp(LETTER_TOKENS.source, LETTER_TOKENS.flags);
  return [...text.matchAll(re)].map((m) => m[0]);
}

function hasPlausibleNameTokens(text) {
  const raw = letterTokenMatches(text);
  if (raw.length === 0) return false;

  const tokens = raw.filter((t) => t.length >= 2);
  const initials = raw.filter((t) => t.length === 1);
  const hasTwoParts = tokens.length >= 2 || (tokens.length >= 1 && initials.length >= 1);
  if (!hasTwoParts) return false;

  const allParts = [...tokens, ...initials];
  let nameLike = 0;
  let roleOrStop = 0;
  for (const t of allParts) {
    const tl = t.toLowerCase();
    if (NAME_PARTICLES.has(tl)) {
      nameLike += 1;
      continue;
    }
    if (ROLE_WORDS.has(tl) || STOP_WORDS.has(tl)) roleOrStop += 1;
    else nameLike += 1;
  }
  if (nameLike < 2) return false;
  if (roleOrStop >= allParts.length) return false;
  if (roleOrStop * 2 >= allParts.length && allParts.length > 2) return false;
  return true;
}

/** @param {string | null | undefined} value */
export function isLikelyPrincipalInvestigatorPersonName(value) {
  if (value == null || typeof value !== 'string' || !value.trim()) return false;
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();
  if (lower.includes('head of')) return false;
  if (lower.includes('senior consultant')) return false;
  if (lower.startsWith('leading ')) return false;
  if (ROLE_LEAD.test(trimmed) && !HONORIFIC_PREFIX.test(trimmed)) return false;
  if (lower.startsWith('principal investigator') && !HONORIFIC_PREFIX.test(trimmed)) return false;

  const honorMatch = trimmed.match(HONORIFIC_PREFIX);
  if (honorMatch) {
    const remainder = honorMatch[2] || '';
    return hasPlausibleNameTokens(remainder);
  }
  return hasPlausibleNameTokens(trimmed);
}
